import React from 'react';

import { Link } from 'react-router-dom';

import { motion } from 'framer-motion';
import {
  ArrowRight,
  BarChart,
  Bot,
  CheckCircle,
  Compass,
  Globe,
  Monitor,
  Shield,
  Star,
  TrendingUp,
  Users,
  Wrench,
} from 'lucide-react';

import { Service } from '../types/serviceTypes';
import EnhancedServiceCard from './ui/EnhancedServiceCard';
import ModernButton from './ui/ModernButton';

interface FeaturedServicesProps {
  className?: string;
}

// Definición de los servicios destacados
const featuredServices: Service[] = [
  {
    id: 'strategic-partner',
    icon: <Compass />,
    title: 'Socio Estratégico',
    description:
      'Acompañamiento integral y personalizado para la toma de decisiones estratégicas y el crecimiento sostenible de su negocio.',
    category: 'estrategicos',
    features: [
      'Asesoramiento ejecutivo continuo',
      'Análisis estratégico de mercado',
      'Planificación de crecimiento a largo plazo',
      'Optimización de procesos críticos',
      'Implementación de soluciones innovadoras',
    ],
    price: '',
  },
  {
    id: 'web-dev',
    icon: <Monitor />,
    title: 'Desarrollo Web y E-commerce',
    description: 'Sitios web modernos y tiendas online optimizadas para conversión.',
    category: 'digitales',
    features: [
      'Diseño responsivo',
      'SEO técnico',
      'Integración de pagos',
      'Google My Business',
      'Identidad visual básica',
    ],
    price: '',
  },
  {
    id: 'presencia-digital',
    icon: <Globe />,
    title: 'Presencia Digital Profesional',
    description:
      'Establecimiento y optimización de la presencia digital de su empresa en las principales plataformas y directorios de servicios.',
    category: 'digitales',
    features: [
      'Registro en plataformas clave',
      'Optimización de perfiles',
      'Gestión de reputación online',
      'Estrategia de contenido',
      'Monitoreo de visibilidad',
    ],
    price: '',
  },
  {
    id: 'web-support',
    icon: <Wrench />,
    title: 'Soporte y Administración Web',
    description:
      'Mantenimiento continuo y soporte técnico para garantizar el óptimo funcionamiento de su presencia digital.',
    category: 'soporte',
    features: [
      'Mantenimiento preventivo',
      'Actualizaciones de seguridad',
      'Copias de seguridad',
      'Soporte técnico prioritario',
      'Monitoreo de rendimiento',
    ],
    price: '',
  },
  {
    id: 'automation-basic',
    icon: <Bot />,
    title: 'Automatización de Procesos',
    description:
      'Automatización de procesos repetitivos para mejorar la eficiencia y productividad de su empresa.',
    category: 'digitales',
    features: [
      'Automatización de tareas',
      'Integración de sistemas',
      'Reportes automáticos',
      'Flujos de trabajo',
      'Optimización de recursos',
    ],
    price: '',
  },
];

const FeaturedServices: React.FC<FeaturedServicesProps> = ({ className = '' }) => {
  // Separar el servicio estrella (Socio Estratégico) del resto
  const starService = featuredServices[0];
  const otherServices = featuredServices.slice(1);

  return (
    <section
      className={`py-16 bg-gradient-to-br from-blue-50 via-white to-blue-50 rounded-xl my-8 shadow-lg ${className}`}
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <motion.span
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white text-sm font-medium px-6 py-2 rounded-full uppercase tracking-wider inline-block mb-4 shadow-md"
          >
            <span className="flex items-center justify-center">
              <Star className="h-4 w-4 mr-2 text-yellow-300 animate-pulse" />
              Servicios Destacados
            </span>
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-3xl md:text-5xl font-bold bg-clip-text text-transparent bg-gradient-to-r from-blue-800 to-blue-600 mb-4"
          >
            Soluciones Exclusivas para su Empresa
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
          >
            Descubra nuestros servicios premium diseñados para maximizar el potencial y rentabilidad
            de su negocio
          </motion.p>
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-6 max-w-2xl mx-auto bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 text-left shadow-sm"
          >
            <p className="text-blue-800 text-sm italic">
              <span className="font-semibold">Valor diferencial:</span> Nuestros servicios
              exclusivos están respaldados por más de 10 años de experiencia y un equipo de
              especialistas certificados, garantizando resultados excepcionales y un retorno de
              inversión superior al promedio del mercado.
            </p>
          </motion.div>
        </div>

        {/* Servicio Estrella - Socio Estratégico */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mb-12"
          whileHover={{ scale: 1.01 }}
        >
          <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-2xl overflow-hidden shadow-2xl relative transform transition-all duration-500 hover:shadow-blue-200">
            {/* Elementos decorativos de fondo */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mt-20 -mr-20"></div>
            <div className="absolute bottom-0 left-0 w-40 h-40 bg-white opacity-5 rounded-full -mb-10 -ml-10"></div>
            <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-yellow-400 opacity-10 rounded-full transform -translate-y-1/2"></div>
            <div className="absolute bottom-1/4 right-1/3 w-24 h-24 bg-blue-300 opacity-10 rounded-full"></div>

            {/* Partículas decorativas animadas */}
            <div
              className="absolute top-1/4 right-1/4 w-3 h-3 bg-yellow-300 rounded-full opacity-70 animate-pulse"
              style={{ animationDuration: '4s' }}
            ></div>
            <div
              className="absolute bottom-1/3 left-1/3 w-2 h-2 bg-blue-200 rounded-full opacity-80 animate-pulse"
              style={{ animationDuration: '3.5s' }}
            ></div>
            <div
              className="absolute top-2/3 right-1/2 w-4 h-4 bg-indigo-300 rounded-full opacity-60 animate-pulse"
              style={{ animationDuration: '5s' }}
            ></div>

            <div className="relative z-10 p-8 md:p-12 flex flex-col md:flex-row items-center">
              <div className="md:w-2/3 mb-8 md:mb-0 md:pr-8">
                <div className="flex items-center mb-4">
                  <div className="bg-gradient-to-r from-yellow-400 to-yellow-300 text-blue-900 px-5 py-1.5 rounded-full flex items-center shadow-lg transform transition-all duration-300 hover:scale-105">
                    <Star className="h-4 w-4 mr-1 animate-pulse" />
                    <span className="text-sm font-bold tracking-wide">Servicio Estrella</span>
                  </div>
                </div>

                <h3 className="text-3xl md:text-4xl font-bold text-white mb-4 bg-clip-text bg-gradient-to-r from-white to-blue-100">
                  {starService.title}
                </h3>
                <p className="text-blue-100 text-lg mb-6 leading-relaxed">
                  {starService.description}
                </p>

                <div className="mb-6 relative">
                  <div className="absolute -left-2 top-0 bottom-0 w-1 bg-gradient-to-b from-yellow-400 via-blue-400 to-indigo-400 rounded-full opacity-70"></div>
                  <h4 className="text-white text-lg font-semibold mb-3 flex items-center">
                    <span className="bg-blue-600 h-2 w-2 rounded-full mr-2"></span>
                    Beneficios exclusivos:
                  </h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
                    {starService.features.map((feature, index) => (
                      <motion.li
                        key={index}
                        className="flex items-center text-white group"
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.1 }}
                        whileHover={{ x: 5 }}
                      >
                        <div className="bg-gradient-to-r from-yellow-400 to-yellow-300 p-1.5 rounded-full mr-2 flex-shrink-0 shadow-sm group-hover:shadow-md transition-all duration-300">
                          <svg
                            className="h-3.5 w-3.5 text-blue-800"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="group-hover:text-blue-200 transition-colors duration-300">
                          {feature}
                        </span>
                      </motion.li>
                    ))}
                  </ul>
                  <p className="text-blue-200 text-sm mt-4 italic bg-blue-800 bg-opacity-30 p-3 rounded-lg">
                    <span className="font-semibold">ROI Estimado:</span> Nuestros clientes reportan
                    un incremento promedio del 35% en eficiencia operativa y un 28% en rentabilidad
                    tras 6 meses de implementación.
                  </p>
                </div>

                <Link to={`/servicios/${starService.id}`}>
                  <ModernButton
                    variant="primary"
                    size="md"
                    className="bg-white hover:bg-blue-50 shadow-md hover:shadow-xl transform hover:-translate-y-1 transition-all duration-300 text-blue-700 group"
                  >
                    <span className="flex items-center">
                      Conocer más
                      <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </span>
                  </ModernButton>
                </Link>
              </div>

              <div className="md:w-1/3 flex justify-center">
                <div className="relative">
                  {/* Efectos visuales para el ícono */}
                  <div
                    className="absolute inset-0 bg-blue-500 bg-opacity-20 rounded-full animate-pulse"
                    style={{ animationDuration: '3s' }}
                  ></div>
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-400 to-indigo-500 rounded-full opacity-30 blur-xl"></div>
                  <div className="relative bg-white p-6 rounded-full shadow-lg backdrop-blur-sm bg-opacity-90 hover:bg-opacity-100 transition-all duration-300 transform hover:scale-105">
                    <Compass className="h-20 w-20 text-blue-600 animate-float" />
                  </div>
                  <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                    <div
                      className="w-24 h-24 border-2 border-blue-300 border-opacity-50 rounded-full animate-spin-slow"
                      style={{ animationDuration: '15s' }}
                    ></div>
                  </div>
                  <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center">
                    <div
                      className="w-32 h-32 border border-yellow-400 border-opacity-30 rounded-full animate-spin-slow"
                      style={{ animationDuration: '20s', animationDirection: 'reverse' }}
                    ></div>
                  </div>
                  {/* Estrellas decorativas */}
                  <div
                    className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-400 rounded-full opacity-70 animate-pulse"
                    style={{ animationDuration: '2s' }}
                  ></div>
                  <div
                    className="absolute -bottom-2 -left-2 w-6 h-6 bg-yellow-400 rounded-full opacity-50 animate-pulse"
                    style={{ animationDuration: '3s' }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Otros Servicios Destacados */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {otherServices.map((service, index) => {
            // Definir estadísticas específicas para cada servicio
            const serviceStats = {
              'web-dev': [
                {
                  label: 'Conversión',
                  value: '+40%',
                  icon: <TrendingUp className="h-3 w-3 text-green-500" />,
                },
                {
                  label: 'Usabilidad',
                  value: '+60%',
                  icon: <Users className="h-3 w-3 text-blue-500" />,
                },
                {
                  label: 'Tráfico',
                  value: '+55%',
                  icon: <BarChart className="h-3 w-3 text-purple-500" />,
                },
              ],
              'presencia-digital': [
                {
                  label: 'Visibilidad',
                  value: '+65%',
                  icon: <Globe className="h-3 w-3 text-blue-500" />,
                },
                {
                  label: 'Clientes',
                  value: '+45%',
                  icon: <Users className="h-3 w-3 text-green-500" />,
                },
                {
                  label: 'Conversión',
                  value: '+35%',
                  icon: <BarChart className="h-3 w-3 text-yellow-500" />,
                },
              ],
              'web-support': [
                {
                  label: 'Uptime',
                  value: '99.9%',
                  icon: <TrendingUp className="h-3 w-3 text-green-500" />,
                },
                {
                  label: 'Seguridad',
                  value: '+75%',
                  icon: <Shield className="h-3 w-3 text-blue-500" />,
                },
                {
                  label: 'Velocidad',
                  value: '+50%',
                  icon: <BarChart className="h-3 w-3 text-yellow-500" />,
                },
              ],
              'automation-basic': [
                {
                  label: 'Eficiencia',
                  value: '+70%',
                  icon: <TrendingUp className="h-3 w-3 text-green-500" />,
                },
                {
                  label: 'Precisión',
                  value: '+85%',
                  icon: <CheckCircle className="h-3 w-3 text-blue-500" />,
                },
                {
                  label: 'Ahorro',
                  value: '+45%',
                  icon: <BarChart className="h-3 w-3 text-yellow-500" />,
                },
              ],
            };

            // Definir beneficios específicos para cada servicio
            const serviceBenefits = {
              'web-dev': [
                'Aumento de conversiones en línea',
                'Experiencia de usuario optimizada',
                'Posicionamiento SEO mejorado',
                'Presencia digital profesional',
              ],
              'presencia-digital': [
                'Aumento de visibilidad en búsquedas locales',
                'Mejora de credibilidad y confianza',
                'Captación de clientes 24/7',
                'Ventaja competitiva en su sector',
              ],
              'web-support': [
                'Reducción de tiempo de inactividad',
                'Protección contra amenazas de seguridad',
                'Respuesta rápida ante incidencias',
                'Rendimiento optimizado constante',
              ],
              'automation-basic': [
                'Reducción de errores humanos',
                'Optimización del tiempo del personal',
                'Escalabilidad de procesos',
                'Análisis de datos mejorado',
              ],
            };

            return (
              <motion.div
                key={service.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.8 + index * 0.1 }}
                whileHover={{ y: -5, transition: { duration: 0.2 } }}
              >
                <EnhancedServiceCard
                  service={service}
                  isFeatured={true}
                  isHighlighted={true}
                  showStats={true}
                  customStats={serviceStats[service.id as keyof typeof serviceStats]}
                  customBenefits={serviceBenefits[service.id as keyof typeof serviceBenefits]}
                />
              </motion.div>
            );
          })}
        </div>

        {/* CTA para ver todos los servicios */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="text-center mt-12"
        >
          <Link
            to="/servicios"
            className="inline-flex items-center bg-blue-600 text-white font-medium px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Ver todos nuestros servicios
            <ArrowRight className="ml-2 h-5 w-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default FeaturedServices;
